import React, { useCallback, useEffect } from 'react';
import { useFocusEffect } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { Bag } from '@order/features-ordering';
import { getStripeKeyPrefix, StripeElementsProvider } from '@order/integrations-stripe';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useDeliveryOrderInFlight } from '../../hooks';
import { handleHardwareBackButtonPress } from '../../navigation';
import { BagAnimatedDialog, BagContent } from './components';
import {
  useBagGestures,
  useBagCartPollingControl,
  useOrderState,
  useWantedTimePollingControl,
} from './hooks';

export const BagScreen = () => {
  const orderState = useOrderState();
  const { formatMessage } = useIntl();

  const {
    cart,
    isFetchingInitialData,
    isSubmittingOrder,
    isShowingModal,
    isShowingGroupOrderConvertModal,
    isShowingGroupOrderShareModal,
    isShowingGroupOrderLeaveModal,
    isShowingGroupOrderLockModal,
    startWantedTimesPolling,
    stopWantedTimesPolling,
    startBagCartPolling,
    stopBagCartPolling,
    startOrdering,
    setStripeFailedToLoad,
    cancelRequest,
    closeBag,
    unlockCart,
    purchaseAttempt,
    stripeSession,
    total,
  } = orderState;

  const isLoggedIn = useIsLoggedIn();
  const isBagReady = !isFetchingInitialData && cart?.orderId;
  const isShowingGroupOrderModal =
    isShowingGroupOrderConvertModal ||
    isShowingGroupOrderShareModal ||
    isShowingGroupOrderLeaveModal ||
    isShowingGroupOrderLockModal;

  const shouldRenderAnimatedDialog = Boolean(
    isBagReady && isLoggedIn && stripeSession && !isShowingGroupOrderModal,
  );

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  // Restart the ordering machine on auth changes to refetch the data with auth state.
  const restartOrderingMachine = useCallback(() => {
    startOrdering();
  }, [startOrdering, isLoggedIn]); // oxlint-disable-line react-hooks/exhaustive-deps

  // Prevent closing bag while submitting an order and close the modal first if it is open.
  const handleCloseBag = useCallback(() => {
    if (isSubmittingOrder) return;

    if (isShowingModal) {
      cancelRequest();
      return;
    }

    closeBag();
  }, [isSubmittingOrder, isShowingModal, cancelRequest, closeBag]);

  const handleBagFocus = useCallback(() => {
    return unlockCart;
  }, [unlockCart]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  // Updates the in-flight delivery order status.
  useDeliveryOrderInFlight();

  // Enable/disable gestures when modal is shown.
  useBagGestures(isShowingModal);

  // Fetch ordering data on bag focus.
  useFocusEffect(restartOrderingMachine);

  useFocusEffect(handleBagFocus);

  // Cancel bag actions if the customer presses the hardware back button.
  const beforeScreenRemoval = useCallback(() => {
    if (isShowingModal) {
      cancelRequest();

      return true;
    }

    return false;
  }, [isShowingModal, cancelRequest]);

  useEffect(() => {
    return handleHardwareBackButtonPress(beforeScreenRemoval);
  }, [beforeScreenRemoval]);

  // Manage wanted times polling.
  useWantedTimePollingControl({
    startWantedTimesPolling,
    stopWantedTimesPolling,
  });

  // Manage cart polling.
  useBagCartPollingControl({
    startBagCartPolling,
    stopBagCartPolling,
  });

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <Bag.DynamicModal
      accessibilityLabel={formatMessage(messages.dialogA11yLabel)}
      onClose={handleCloseBag}
    >
      <BagContent orderState={orderState} />

      {shouldRenderAnimatedDialog ? (
        <StripeElementsProvider
          source="bag"
          amount={total}
          sessionId={stripeSession}
          onFailedToLoadStripe={setStripeFailedToLoad}
        >
          <BagAnimatedDialog key={getStripeKeyPrefix(purchaseAttempt)} orderState={orderState} />
        </StripeElementsProvider>
      ) : null}
    </Bag.DynamicModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dialogA11yLabel: {
    defaultMessage: 'Bag',
    description: 'Bag | Dialog a11y label',
  },
});
